import React, { useState } from 'react';
import { MapPin, Clock, Check } from 'lucide-react';
import { northernLightsExperiences } from '../mock';
import ExperienceCard from '../components/ExperienceCard';
import NewsletterSignup from '../components/NewsletterSignup';
import { Button } from '../components/ui/button';

const NorthernLights = () => {
  const [newsletterOpen, setNewsletterOpen] = useState(false);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1673712277551-ea12f7a11a71"
          alt="Northern Lights Photography"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-light text-white mb-6">
            Northern Lights Experiences
          </h1>
          <p className="text-xl text-white/90 leading-relaxed">
            Small groups. Patient teaching. Extraordinary skies.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xl text-gray-700 leading-relaxed mb-6">
            The Northern Lights are our winter compass. These experiences take you to some of the world's best aurora viewing locations — Norway, Sweden, Finland, Iceland, Alaska, and Canada.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed">
            In small groups of 4-5 people, you'll learn the basics of photographing the aurora, understand the science behind this natural phenomenon, and most importantly, experience the wonder of standing under dancing lights in dark winter skies.
          </p>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 bg-brand-light">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 mb-8 text-center">
            What's Included
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              'Comfortable lodging throughout your stay',
              'Airport transfers to and from accommodation',
              'Small group size (4-5 people maximum)',
              'Photography basics workshop',
              'Nightly aurora hunting expeditions',
              'Northern Lights observational tips',
              'Basic science behind the aurora',
              'Patient, beginner-friendly instruction'
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-3 bg-white p-4 rounded-lg shadow-sm">
                <Check className="text-brand-teal mt-1 flex-shrink-0" size={20} />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experiences Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-light text-gray-900 mb-12 text-center">
            Choose Your Aurora Adventure
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            {northernLightsExperiences.map((experience) => (
              <ExperienceCard key={experience.id} experience={experience} />
            ))}
          </div>
        </div>
      </section>

      {/* Photography Section */}
      <section className="py-16 bg-brand-light">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1509529711801-deac231925ac"
                alt="Aurora photography"
                className="rounded-lg shadow-lg w-full h-80 object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl font-light text-gray-900 mb-6">
                Beginner-Friendly Photography
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Never touched manual mode? Perfect. These trips are designed for people who want to learn photography by doing it, not by wading through technical jargon.
              </p>
              <p className="text-gray-600 leading-relaxed">
                We focus on understanding light, composition, and actually seeing what's in front of you. Camera settings come second. Confidence grows through participation and shared discovery.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Info */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-light text-gray-900 mb-6">
            Honest, Accessible Pricing
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-8">
            Our Northern Lights experiences range from <strong>£1,998 to £2,500</strong>, depending on location and duration. This includes lodging, transfers, instruction, and nightly aurora expeditions.
          </p>
          <p className="text-gray-600 leading-relaxed">
            We believe extraordinary experiences shouldn't require luxury pricing. These trips are designed to be accessible to curious explorers without the intimidation or cost of traditional photography tours.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-brand-navy text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-light mb-6">
            Ready to Chase the Lights?
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Get started with our free <strong>Aurora Readiness Guide</strong> or reach out to discuss which experience is right for you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => setNewsletterOpen(true)}
              className="bg-brand-mint text-brand-navy hover:bg-white px-8 py-6 text-base"
            >
              Get Free Aurora Guide
            </Button>
          </div>
        </div>
      </section>

      <NewsletterSignup isOpen={newsletterOpen} onClose={() => setNewsletterOpen(false)} />
    </div>
  );
};

export default NorthernLights;